<template>
  <aside class="ren-decoration-palette ren-panel" :class="`is-${viewMode}`">
    <div class="ren-decoration-segmented" role="tablist" aria-label="装修资源">
      <button
        type="button"
        role="tab"
        :aria-selected="activeTab === 'components'"
        class="ren-decoration-segmented__item"
        :class="{ 'is-active': activeTab === 'components', active: activeTab === 'components' }"
        @click="emit('update:activeTab', 'components')"
      >
        组件库
      </button>
      <button
        v-if="templatesEnabled"
        type="button"
        role="tab"
        :aria-selected="activeTab === 'templates'"
        class="ren-decoration-segmented__item"
        :class="{ 'is-active': activeTab === 'templates', active: activeTab === 'templates' }"
        @click="emit('update:activeTab', 'templates')"
      >
        模板市场
      </button>
    </div>

    <template v-if="activeTab === 'components'">
      <section v-if="shellPage" class="ren-decoration-shell-source">
        <div>
          <strong>{{ shellTitle }}</strong>
          <span>{{ shellDescription }}</span>
        </div>
        <RrButton v-if="shellActionVisible" type="primary" size="small" @click="emit('open-shell-page')">
          装修当前页面
        </RrButton>
      </section>

      <template v-else>
        <!-- 头部工具栏：搜索框 + 全部折叠/展开 + 视图模式切换 -->
        <div class="ren-decoration-library-toolbar">
          <div class="ren-decoration-library-search">
            <RrInput
              v-model:value.trim="keyword"
              placeholder="搜索组件名称或类型..."
              allow-clear
              size="small"
              aria-label="搜索组件"
            >
              <template #prefix>
                <Search :size="14" class="ren-decoration-library-search__icon" aria-hidden="true" />
              </template>
            </RrInput>
          </div>
          <div class="ren-decoration-library-actions">
            <button
              type="button"
              class="ren-decoration-tool-btn"
              :title="isAllCollapsed ? '展开全部' : '折叠全部'"
              :aria-label="isAllCollapsed ? '展开全部' : '折叠全部'"
              @click="toggleAllGroups"
            >
              <ChevronsUpDown :size="14" />
            </button>
            <div class="ren-decoration-view-toggle">
              <button
                type="button"
                class="ren-decoration-tool-btn"
                :class="{ 'is-active': viewMode === 'grid' }"
                title="网格视图（紧凑）"
                aria-label="网格视图"
                @click="setViewMode('grid')"
              >
                <LayoutGrid :size="14" />
              </button>
              <button
                type="button"
                class="ren-decoration-tool-btn"
                :class="{ 'is-active': viewMode === 'list' }"
                title="列表视图（详细）"
                aria-label="列表视图"
                @click="setViewMode('list')"
              >
                <List :size="14" />
              </button>
            </div>
          </div>
        </div>

        <!-- 分类胶囊栏 (Pills) -->
        <div v-if="categoryOptions.length > 1 && !keyword" class="ren-decoration-category-bar">
          <button
            v-show="canScrollLeft"
            type="button"
            class="ren-decoration-pill-arrow is-left"
            aria-label="向左滑动分类"
            title="向左滑动"
            @click="scrollPills('left')"
          >
            <ChevronLeft :size="12" />
          </button>
          <div
            ref="pillsContainerRef"
            class="ren-decoration-category-pills"
            @wheel="onPillsWheel"
            @scroll="onPillsScroll"
            @mousedown="onPillsMouseDown"
          >
            <button
              v-for="cat in categoryOptions"
              :key="cat.key"
              type="button"
              class="ren-decoration-pill"
              :class="{ 'is-active': activeCategory === cat.key }"
              @click="selectCategory(cat.key, $event)"
            >
              <span>{{ cat.label }}</span>
              <span class="ren-decoration-pill__badge">{{ cat.count }}</span>
            </button>
          </div>
          <button
            v-show="canScrollRight"
            type="button"
            class="ren-decoration-pill-arrow is-right"
            aria-label="向右滑动分类"
            title="向右滑动"
            @click="scrollPills('right')"
          >
            <ChevronRight :size="12" />
          </button>
        </div>

        <div class="ren-decoration-library-scroll-area">
          <!-- ⭐ 最近使用分组（仅在未搜索且在全部下展示） -->
          <div
            v-if="recentItems.length > 0 && !keyword && activeCategory === 'ALL'"
            class="ren-decoration-palette__group is-recent"
          >
            <div class="ren-decoration-palette__group-header" @click="toggleGroup('__recent__')">
              <div class="group-header-left">
                <component
                  :is="collapsedGroupKeys.has('__recent__') ? ChevronRight : ChevronDown"
                  :size="14"
                  class="group-header-icon"
                />
                <Clock :size="13" class="recent-icon" />
                <h4 class="group-header-title">最近使用</h4>
                <span class="group-header-badge">{{ recentItems.length }}</span>
              </div>
              <button
                type="button"
                class="group-header-clear"
                title="清空最近记录"
                @click.stop="clearRecent"
              >
                清空
              </button>
            </div>

            <div
              v-show="!collapsedGroupKeys.has('__recent__')"
              :class="viewMode === 'grid' ? 'ren-decoration-palette__grid' : 'ren-decoration-palette__list'"
            >
              <button
                v-for="item in recentItems"
                :key="`recent-${item.type}`"
                class="ren-decoration-palette__item"
                :disabled="readonly"
                draggable="true"
                type="button"
                :data-component-scope="item.componentScope"
                :data-owner-module-code="item.ownerModuleCode"
                :data-supported-platforms="item.supportedPlatforms?.join(',')"
                @click="handleItemClick(item)"
                @dragstart="handleDragStart(item, $event)"
                @dragend="onDragEnd"
                @mouseenter="onItemMouseEnter(item, $event)"
                @mouseleave="onItemMouseLeave"
              >
                <i aria-hidden="true">
                  <slot name="icon" :item="item">{{ (item.icon && item.icon.length <= 2) ? item.icon : item.label.slice(0, 1) }}</slot>
                </i>
                <span class="ren-decoration-palette__item-body">
                  <strong :title="item.label">{{ item.label }}</strong>
                  <small v-if="viewMode === 'list'">{{ item.description }}</small>
                </span>
                <span class="ren-decoration-palette__item-action" title="添加组件" aria-hidden="true">
                  <Plus :size="viewMode === 'grid' ? 12 : 14" />
                </span>
              </button>
            </div>
          </div>

          <!-- 各组件分类与分组列表 -->
          <div
            v-for="group in filteredGroups"
            :key="group.key"
            class="ren-decoration-palette__group"
          >
            <div class="ren-decoration-palette__group-header" @click="toggleGroup(group.key)">
              <div class="group-header-left">
                <component
                  :is="(collapsedGroupKeys.has(group.key) && !keyword) ? ChevronRight : ChevronDown"
                  :size="14"
                  class="group-header-icon"
                />
                <h4 class="group-header-title">{{ group.label }}</h4>
                <small
                  v-if="group.scope === 'BUSINESS' && group.ownerModuleCode && group.ownerModuleCode !== group.label"
                  class="group-header-module"
                >
                  {{ group.ownerModuleCode }}
                </small>
                <span class="group-header-badge">{{ group.items.length }}</span>
              </div>
            </div>

            <div
              v-show="!collapsedGroupKeys.has(group.key) || Boolean(keyword)"
              :class="viewMode === 'grid' ? 'ren-decoration-palette__grid' : 'ren-decoration-palette__list'"
            >
              <button
                v-for="item in group.items"
                :key="item.type"
                class="ren-decoration-palette__item"
                :disabled="readonly"
                draggable="true"
                type="button"
                :data-component-scope="item.componentScope"
                :data-owner-module-code="item.ownerModuleCode"
                :data-supported-platforms="item.supportedPlatforms?.join(',')"
                @click="handleItemClick(item)"
                @dragstart="handleDragStart(item, $event)"
                @dragend="onDragEnd"
                @mouseenter="onItemMouseEnter(item, $event)"
                @mouseleave="onItemMouseLeave"
              >
                <i aria-hidden="true">
                  <slot name="icon" :item="item">{{ (item.icon && item.icon.length <= 2) ? item.icon : item.label.slice(0, 1) }}</slot>
                </i>
                <span class="ren-decoration-palette__item-body">
                  <strong :title="item.label">{{ item.label }}</strong>
                  <small v-if="viewMode === 'list'">{{ item.description }}</small>
                </span>
                <span class="ren-decoration-palette__item-action" title="添加组件" aria-hidden="true">
                  <Plus :size="viewMode === 'grid' ? 12 : 14" />
                </span>
              </button>
            </div>
          </div>

          <div v-if="filteredGroups.length === 0" class="ren-decoration-empty-wrap">
            <RrEmpty description="暂无符合条件的组件" />
            <RrButton v-if="keyword" size="small" @click="keyword = ''">
              清空搜索
            </RrButton>
          </div>
        </div>

        <!-- 悬停预览浮层 (Hover Popover Preview) -->
        <Teleport to="body">
          <Transition name="rr-fade">
            <div
              v-if="hoveredItem && !isDragging && viewMode === 'grid'"
              class="ren-decoration-preview-popover"
              :style="{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px` }"
            >
              <div class="popover-head">
                <span class="popover-label">{{ hoveredItem.label }}</span>
                <span class="popover-type">{{ hoveredItem.type }}</span>
              </div>
              <p class="popover-desc">{{ hoveredItem.description || '暂无组件描述' }}</p>
              <div class="popover-foot">
                <span v-if="hoveredItem.ownerModuleCode" class="popover-module">
                  {{ hoveredItem.ownerModuleCode }}
                </span>
                <span class="popover-hint">单击或拖拽添加</span>
              </div>
            </div>
          </Transition>
        </Teleport>
      </template>
    </template>

    <slot v-else name="templates" />
  </aside>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  Search,
  LayoutGrid,
  List,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
  ChevronsUpDown,
  Plus
} from '@lucide/vue'
import { RrButton, RrInput, RrEmpty } from '@xjlv2/ui'
import type { RenDecorationComponentDefinition, RenDecorationPaletteItem } from '../../types'
import { groupDecorationPalette, type DecorationPaletteGroup } from '../utils/editor-catalog'

const props = withDefaults(defineProps<{
  activeTab?: 'components' | 'templates'
  definitions?: RenDecorationComponentDefinition[]
  items?: RenDecorationPaletteItem[]
  eyebrow?: string
  title?: string
  subtitle?: string
  readonly?: boolean
  templatesEnabled?: boolean
  shellPage?: boolean
  shellTitle?: string
  shellDescription?: string
  shellActionVisible?: boolean
  defaultViewMode?: 'grid' | 'list'
}>(), {
  activeTab: 'components',
  definitions: () => [],
  items: () => [],
  eyebrow: '',
  title: '',
  subtitle: '',
  readonly: false,
  templatesEnabled: true,
  shellPage: false,
  shellTitle: '',
  shellDescription: '',
  shellActionVisible: false,
  defaultViewMode: 'grid'
})

const emit = defineEmits<{
  'update:activeTab': [value: 'components' | 'templates']
  add: [item: RenDecorationPaletteItem]
  drag: [type: string]
  'open-shell-page': []
}>()

const keyword = ref('')
const platform = ref<'UNI_APP' | 'APP'>('UNI_APP')
const activeCategory = ref<string>('ALL')

// 视图模式：默认双列紧凑网格（grid），升级到 v2 key 避免旧缓存干扰
const VIEW_MODE_KEY = 'ren_decoration_palette_view_mode_v2'
const RECENT_KEY = 'ren_decoration_recent_components'

const viewMode = ref<'grid' | 'list'>((() => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const saved = window.localStorage.getItem(VIEW_MODE_KEY)
    if (saved === 'list' || saved === 'grid') return saved
  }
  return props.defaultViewMode || 'grid'
})())

function setViewMode(mode: 'grid' | 'list') {
  viewMode.value = mode
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(VIEW_MODE_KEY, mode)
    }
  } catch {}
}

// 分类胶囊横向滑动与滚动控制器
const pillsContainerRef = ref<HTMLElement | null>(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
let isDraggingPills = false
let pillsStartX = 0
let pillsScrollLeftStart = 0
let pillsMoved = false

function updateScrollState() {
  const el = pillsContainerRef.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 2
  canScrollRight.value = el.scrollLeft < el.scrollWidth - el.clientWidth - 2
}

function onPillsScroll() {
  updateScrollState()
}

function onPillsWheel(event: WheelEvent) {
  const el = pillsContainerRef.value
  if (!el) return
  if (Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
    if (
      (event.deltaY > 0 && el.scrollLeft < el.scrollWidth - el.clientWidth - 1) ||
      (event.deltaY < 0 && el.scrollLeft > 0)
    ) {
      event.preventDefault()
      el.scrollLeft += event.deltaY
      updateScrollState()
    }
  }
}

function scrollPills(direction: 'left' | 'right') {
  const el = pillsContainerRef.value
  if (!el) return
  const offset = direction === 'left' ? -140 : 140
  el.scrollBy({ left: offset, behavior: 'smooth' })
  setTimeout(updateScrollState, 250)
}

function onPillsMouseDown(event: MouseEvent) {
  const el = pillsContainerRef.value
  if (!el) return
  isDraggingPills = true
  pillsMoved = false
  pillsStartX = event.pageX - el.offsetLeft
  pillsScrollLeftStart = el.scrollLeft

  const onMouseMove = (e: MouseEvent) => {
    if (!isDraggingPills || !el) return
    const x = e.pageX - el.offsetLeft
    const diff = (x - pillsStartX) * 1.2
    if (Math.abs(diff) > 4) {
      pillsMoved = true
    }
    el.scrollLeft = pillsScrollLeftStart - diff
    updateScrollState()
  }

  const onMouseUp = () => {
    isDraggingPills = false
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

function selectCategory(key: string, event: MouseEvent) {
  if (pillsMoved) {
    pillsMoved = false
    return
  }
  activeCategory.value = key
  const target = event.currentTarget as HTMLElement | null
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }
}

// 最近使用组件持久化
const recentTypes = ref<string[]>((() => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(RECENT_KEY)
      if (raw) return JSON.parse(raw) as string[]
    }
  } catch {}
  return []
})())

function recordRecent(type: string) {
  try {
    const next = [type, ...recentTypes.value.filter((t) => t !== type)].slice(0, 6)
    recentTypes.value = next
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(next))
    }
  } catch {}
}

function clearRecent() {
  recentTypes.value = []
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(RECENT_KEY)
    }
  } catch {}
}

// 分组与组件
const groups = computed<DecorationPaletteGroup[]>(() => {
  const rawGroups: DecorationPaletteGroup[] = (() => {
    if (props.definitions.length) return groupDecorationPalette(props.definitions, keyword.value, platform.value)
    const normalizedKeyword = keyword.value.toLocaleLowerCase()
    const supportedItems = props.items.filter((item) => {
      const supportedPlatforms = item.supportedPlatforms ?? []
      const matchesPlatform = supportedPlatforms.length === 0 || supportedPlatforms.includes(platform.value)
      const matchesKeyword = !normalizedKeyword || [item.type, item.label, item.description, item.group, item.ownerModuleCode]
        .some((value) => String(value || '').toLocaleLowerCase().includes(normalizedKeyword))
      return matchesPlatform && matchesKeyword
    })
    return Array.from(new Set(supportedItems.map((item) => item.group))).map((label) => ({
      key: label,
      label,
      scope: 'COMMON',
      ownerModuleCode: '',
      items: supportedItems.filter((item) => item.group === label)
    }))
  })()

  // 将通用组件按 item.group (基础组件、导航组件、数据组件、营销组件、辅助组件) 细化拆分，保留业务组件原有分组
  const result: DecorationPaletteGroup[] = []
  const COMMON_ORDER = ['基础组件', '导航组件', '数据组件', '营销组件', '辅助组件']

  for (const group of rawGroups) {
    if (group.scope === 'COMMON') {
      const subMap = new Map<string, RenDecorationPaletteItem[]>()
      for (const item of group.items) {
        const subName = item.group?.trim() || '通用组件'
        const list = subMap.get(subName) ?? []
        list.push(item)
        subMap.set(subName, list)
      }
      for (const [subName, items] of subMap.entries()) {
        result.push({
          key: `COMMON:${subName}`,
          label: subName,
          scope: 'COMMON',
          ownerModuleCode: group.ownerModuleCode,
          items
        })
      }
    } else {
      result.push(group)
    }
  }

  return result.sort((left, right) => {
    if (left.scope !== right.scope) return left.scope === 'COMMON' ? -1 : 1
    if (left.scope === 'COMMON' && right.scope === 'COMMON') {
      const leftIndex = COMMON_ORDER.indexOf(left.label)
      const rightIndex = COMMON_ORDER.indexOf(right.label)
      if (leftIndex !== -1 && rightIndex !== -1) return leftIndex - rightIndex
      if (leftIndex !== -1) return -1
      if (rightIndex !== -1) return 1
    }
    return left.label.localeCompare(right.label)
  })
})

// 分类胶囊标签列表
const categoryOptions = computed(() => {
  const allGroups = groups.value
  const total = allGroups.reduce((acc, g) => acc + g.items.length, 0)
  const options = [{ key: 'ALL', label: '全部', count: total }]
  for (const group of allGroups) {
    if (group.items.length > 0) {
      options.push({
        key: group.key,
        label: group.label,
        count: group.items.length
      })
    }
  }
  return options
})

watch(categoryOptions, () => {
  nextTick(updateScrollState)
})

onMounted(() => {
  nextTick(updateScrollState)
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateScrollState)
  }
})

// 根据胶囊标签过滤的分组
const filteredGroups = computed(() => {
  if (!keyword.value && activeCategory.value !== 'ALL') {
    return groups.value.filter((g) => g.key === activeCategory.value)
  }
  return groups.value
})

// 最近使用对应的 items
const recentItems = computed(() => {
  if (recentTypes.value.length === 0) return []
  const allItems = groups.value.flatMap((g) => g.items)
  const itemMap = new Map(allItems.map((item) => [item.type, item]))
  return recentTypes.value.map((type) => itemMap.get(type)).filter(Boolean) as RenDecorationPaletteItem[]
})

// 手风琴折叠管理
const collapsedGroupKeys = ref<Set<string>>(new Set())

function toggleGroup(key: string) {
  if (collapsedGroupKeys.value.has(key)) {
    collapsedGroupKeys.value.delete(key)
  } else {
    collapsedGroupKeys.value.add(key)
  }
}

const isAllCollapsed = computed(() => {
  const keys = filteredGroups.value.map((g) => g.key)
  return keys.length > 0 && keys.every((k) => collapsedGroupKeys.value.has(k))
})

function toggleAllGroups() {
  if (isAllCollapsed.value) {
    collapsedGroupKeys.value.clear()
  } else {
    filteredGroups.value.forEach((g) => collapsedGroupKeys.value.add(g.key))
    if (recentItems.value.length > 0) {
      collapsedGroupKeys.value.add('__recent__')
    }
  }
}

// 悬停浮层状态
const hoveredItem = ref<RenDecorationPaletteItem | null>(null)
const popoverPosition = ref({ top: 0, left: 0 })
const isDragging = ref(false)
let hoverTimer: ReturnType<typeof setTimeout> | null = null

function onItemMouseEnter(item: RenDecorationPaletteItem, event: MouseEvent) {
  if (isDragging.value || viewMode.value !== 'grid') return
  const target = event.currentTarget as HTMLElement | null
  if (!target) return
  const rect = target.getBoundingClientRect()
  if (hoverTimer) clearTimeout(hoverTimer)
  hoverTimer = setTimeout(() => {
    if (isDragging.value) return
    hoveredItem.value = item
    popoverPosition.value = {
      top: Math.max(10, Math.min(rect.top - 4, window.innerHeight - 170)),
      left: rect.right + 10
    }
  }, 160)
}

function onItemMouseLeave() {
  if (hoverTimer) clearTimeout(hoverTimer)
  hoveredItem.value = null
}

function handleItemClick(item: RenDecorationPaletteItem) {
  recordRecent(item.type)
  emit('add', item)
}

function handleDragStart(item: RenDecorationPaletteItem, event: DragEvent) {
  isDragging.value = true
  hoveredItem.value = null
  if (hoverTimer) clearTimeout(hoverTimer)
  recordRecent(item.type)
  onDragStart(item, event)
}

function onDragEnd() {
  isDragging.value = false
}

function onDragStart(item: RenDecorationPaletteItem, event: DragEvent) {
  event.dataTransfer?.setData('application/x-ren-decoration-component', item.type)
  event.dataTransfer?.setData('text/plain', item.type)
  emit('drag', item.type)
}

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateScrollState)
  }
  if (hoverTimer) clearTimeout(hoverTimer)
})
</script>

<style scoped>
/* 基础容器 */
:host,
.ren-decoration-palette {
  overflow: hidden;
}

/* 分段控制器 (Segmented Control) */
.ren-decoration-segmented,
.ren-decoration-tabs {
  display: flex;
  align-items: center;
  padding: 3px;
  background: var(--rr-color-fill-quaternary, #F1F5F9);
  border-radius: var(--rr-radius-control, 8px);
  border: 1px solid var(--rr-color-border, #E2E8F0);
  gap: 2px;
  margin-bottom: 12px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.ren-decoration-segmented__item,
.ren-decoration-tabs button {
  flex: 1 1 0%;
  height: 32px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: calc(var(--rr-radius-control, 8px) - 3px);
  background: transparent;
  color: var(--rr-color-text-muted, #64748B);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  outline: none;
  padding: 0 8px;
}

.ren-decoration-segmented__item:hover:not(.is-active):not(.active),
.ren-decoration-tabs button:hover:not(.active):not(.is-active) {
  color: var(--rr-color-text, #1E293B);
}

.ren-decoration-segmented__item.is-active,
.ren-decoration-segmented__item.active,
.ren-decoration-tabs button.active,
.ren-decoration-tabs button.is-active {
  background: #FFFFFF;
  color: var(--rr-color-primary, #5A46E0);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* 顶部操作工具栏 */
.ren-decoration-library-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.ren-decoration-library-search {
  flex: 1;
  min-width: 0;
}

.ren-decoration-library-search__icon {
  color: var(--rr-color-text-muted, #64748B);
}

.ren-decoration-library-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ren-decoration-view-toggle {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  background: var(--rr-color-fill-quaternary, #F1F5F9);
  border-radius: var(--rr-radius-control, 6px);
  border: 1px solid var(--rr-color-border, #E2E8F0);
}

.ren-decoration-tool-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--rr-color-text-muted, #64748B);
  cursor: pointer;
  transition: all 0.16s ease;
}

.ren-decoration-tool-btn:hover {
  color: var(--rr-color-text, #1E293B);
  background: rgba(0, 0, 0, 0.04);
}

.ren-decoration-tool-btn.is-active {
  background: #FFFFFF;
  color: var(--rr-color-primary, #5A46E0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

/* 分类胶囊滚动栏容器 */
.ren-decoration-category-bar {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--rr-color-border-subtle, #F1F5F9);
  padding-bottom: 6px;
  flex-shrink: 0;
}

.ren-decoration-pill-arrow {
  position: absolute;
  top: calc(50% - 3px);
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: 50%;
  background: var(--rr-color-surface, #FFFFFF);
  color: var(--rr-color-text-muted, #64748B);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;
}

.ren-decoration-pill-arrow:hover {
  color: var(--rr-color-primary, #5A46E0);
  border-color: var(--rr-color-primary, #5A46E0);
  background: var(--rr-color-primary-soft, #EEF2FF);
}

.ren-decoration-pill-arrow.is-left {
  left: 0;
}

.ren-decoration-pill-arrow.is-right {
  right: 0;
}

.ren-decoration-category-pills {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 2px 2px 4px;
}

.ren-decoration-category-pills::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.ren-decoration-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid var(--rr-color-border, #E2E8F0);
  background: var(--rr-color-surface, #FFFFFF);
  color: var(--rr-color-text-secondary, #475569);
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
  transition: all 0.16s ease;
}

.ren-decoration-pill:hover {
  border-color: var(--rr-color-primary, #5A46E0);
  color: var(--rr-color-primary, #5A46E0);
  background: var(--rr-color-primary-soft, #EEF2FF);
}

.ren-decoration-pill.is-active {
  background: var(--rr-color-primary-soft, #EEF2FF);
  border-color: var(--rr-color-primary, #5A46E0);
  color: var(--rr-color-primary, #5A46E0);
  font-weight: 500;
}

.ren-decoration-pill__badge {
  font-size: 10px;
  line-height: 1;
  padding: 1px 4px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
}

.ren-decoration-pill.is-active .ren-decoration-pill__badge {
  background: rgba(90, 70, 224, 0.15);
}

/* 滚动内容区 */
.ren-decoration-library-scroll-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

/* 分组样式与手风琴头部 */
.ren-decoration-palette__group {
  margin-bottom: 14px;
}

.ren-decoration-palette__group.is-recent {
  padding-bottom: 12px;
  border-bottom: 1px dashed var(--rr-color-border, #E2E8F0);
}

.ren-decoration-palette__group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 4px;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  border-radius: 6px;
  transition: background 0.15s ease;
}

.ren-decoration-palette__group-header:hover {
  background: var(--rr-color-fill-quaternary, #F8FAFC);
}

.group-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.group-header-icon {
  color: var(--rr-color-text-muted, #94A3B8);
  flex-shrink: 0;
}

.recent-icon {
  color: var(--rr-color-warning, #946008);
  flex-shrink: 0;
}

.group-header-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text, #1E293B);
  white-space: nowrap;
}

.group-header-module {
  font-size: 11px;
  font-weight: 400;
  color: var(--rr-color-text-muted, #64748B);
  background: var(--rr-color-fill-quaternary, #F1F5F9);
  padding: 1px 6px;
  border-radius: 4px;
}

.group-header-badge {
  font-size: 11px;
  font-weight: 500;
  color: var(--rr-color-text-muted, #94A3B8);
}

.group-header-clear {
  border: none;
  background: transparent;
  color: var(--rr-color-text-muted, #94A3B8);
  font-size: 12px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
}

.group-header-clear:hover {
  color: var(--rr-color-danger, #EF4444);
}

/* 列表展示模式（单列详细） */
.ren-decoration-palette__list {
  display: grid;
  gap: 8px;
}

/* 网格展示模式（双列垂直紧凑卡片：上图标、下文字） */
.ren-decoration-palette__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.ren-decoration-palette__item-body {
  display: block;
  min-width: 0;
  flex: 1;
  text-align: left;
}

.ren-decoration-palette__item-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: var(--rr-color-primary, #5A46E0);
  background: rgba(90, 70, 224, 0.08);
  opacity: 0;
  transform: scale(0.85);
  transition: opacity 0.16s ease, transform 0.16s ease;
  flex-shrink: 0;
}

.ren-decoration-palette__item:hover:not(:disabled) .ren-decoration-palette__item-action {
  opacity: 1;
  transform: scale(1);
}

.is-grid .ren-decoration-palette__item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 70px;
  min-height: 70px;
  padding: 8px 6px 6px;
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: 8px;
  background: var(--rr-color-surface, #FFFFFF);
  cursor: grab;
  transition: border-color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
  box-sizing: border-box;
}

.is-grid .ren-decoration-palette__item:active {
  cursor: grabbing;
  transform: scale(0.97);
}

.is-grid .ren-decoration-palette__item:hover:not(:disabled) {
  border-color: var(--rr-color-primary, #5A46E0);
  background: var(--rr-color-primary-soft, #EEF2FF);
  box-shadow: 0 4px 12px rgba(90, 70, 224, 0.12);
  transform: translateY(-2px);
}

.is-grid .ren-decoration-palette__item i {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 6px;
  background: var(--rr-color-fill-quaternary, #F1F5F9);
  color: var(--rr-color-primary, #5A46E0);
  font-size: 13px;
  transition: background-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
}

.is-grid .ren-decoration-palette__item:hover:not(:disabled) i {
  background: #FFFFFF;
  box-shadow: 0 2px 6px rgba(90, 70, 224, 0.18);
  transform: scale(1.08);
}

.is-grid .ren-decoration-palette__item-body {
  width: 100%;
  min-width: 0;
  text-align: center;
}

.is-grid .ren-decoration-palette__item strong {
  display: block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
  color: var(--rr-color-text, #1E293B);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 2px;
  transition: color 0.16s ease;
}

.is-grid .ren-decoration-palette__item:hover:not(:disabled) strong {
  color: var(--rr-color-primary, #5A46E0);
}

.is-grid .ren-decoration-palette__item .ren-decoration-palette__item-action {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0 1px 3px rgba(90, 70, 224, 0.2);
}

/* 列表模式（详细模式）卡片 */
.is-list .ren-decoration-palette__item {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 56px;
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: 8px;
  background: var(--rr-color-surface, #FFFFFF);
  color: var(--rr-color-text, #1E293B);
  padding: 10px;
  font: inherit;
  text-align: left;
  cursor: grab;
  transition: border-color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.is-list .ren-decoration-palette__item:active {
  cursor: grabbing;
  transform: scale(0.98);
}

.is-list .ren-decoration-palette__item:hover:not(:disabled) {
  border-color: var(--rr-color-primary, #5A46E0);
  background: var(--rr-color-primary-soft, #EEF2FF);
  box-shadow: 0 3px 10px rgba(90, 70, 224, 0.1);
  transform: translateY(-1px);
}

.is-list .ren-decoration-palette__item i {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 8px;
  background: var(--rr-color-primary-soft, #EEF2FF);
  color: var(--rr-color-primary, #5A46E0);
  font-style: normal;
  transition: background-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
}

.is-list .ren-decoration-palette__item:hover:not(:disabled) i {
  background: #FFFFFF;
  box-shadow: 0 1px 4px rgba(90, 70, 224, 0.16);
  transform: scale(1.05);
}

.is-list .ren-decoration-palette__item strong {
  font-size: 13px;
  color: var(--rr-color-text, #1E293B);
  transition: color 0.16s ease;
}

.is-list .ren-decoration-palette__item:hover:not(:disabled) strong {
  color: var(--rr-color-primary, #5A46E0);
}

.is-list .ren-decoration-palette__item span,
.is-list .ren-decoration-palette__item small {
  display: grid;
  min-width: 0;
}

.is-list .ren-decoration-palette__item small {
  color: var(--rr-color-text-muted, #64748B);
  font-size: 12px;
  line-height: 1.35;
}

.is-list .ren-decoration-palette__item .ren-decoration-palette__item-action {
  width: 22px;
  height: 22px;
}

.ren-decoration-shell-source {
  display: grid;
  gap: 12px;
  margin-top: 14px;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: var(--rr-color-surface);
  padding: 12px;
}

.ren-decoration-empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
}
</style>

<style>
/* 全局悬停浮层（通过 Teleport 渲染至 body，避免被侧边栏 overflow 裁切） */
.ren-decoration-preview-popover {
  position: fixed;
  z-index: var(--rr-z-popover, 1200);
  width: 220px;
  padding: var(--rr-space-2, 8px) var(--rr-space-3, 12px);
  border-radius: var(--rr-radius-panel, 8px);
  background: var(--rr-color-text-strong, #171a1f);
  color: #ffffff;
  box-shadow: var(--rr-shadow-overlay, 0 12px 32px rgba(15, 23, 42, 0.16));
  pointer-events: none;
}

.ren-decoration-preview-popover .popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--rr-space-1, 4px);
  gap: var(--rr-space-2, 8px);
}

.ren-decoration-preview-popover .popover-label {
  font-size: var(--rr-font-size-base, 13px);
  font-weight: var(--rr-font-weight-bold, 600);
  color: #ffffff;
}

.ren-decoration-preview-popover .popover-type {
  font-size: var(--rr-font-size-xs, 11px);
  font-family: monospace;
  color: var(--rr-color-text-subtle, #8a94a2);
  background: rgba(255, 255, 255, 0.12);
  padding: 1px var(--rr-space-1, 4px);
  border-radius: var(--rr-radius-xs, 4px);
}

.ren-decoration-preview-popover .popover-desc {
  margin: 0 0 var(--rr-space-2, 8px);
  font-size: var(--rr-font-size-sm, 12px);
  line-height: var(--rr-line-height-base, 1.5);
  color: var(--rr-color-border, #e1e5ea);
}

.ren-decoration-preview-popover .popover-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: var(--rr-space-1, 4px);
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  font-size: var(--rr-font-size-xs, 11px);
}

.ren-decoration-preview-popover .popover-module {
  color: var(--rr-color-primary-border, #c6b9f3);
}

.ren-decoration-preview-popover .popover-hint {
  color: var(--rr-color-text-subtle, #8a94a2);
}

.rr-fade-enter-active,
.rr-fade-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.rr-fade-enter-from,
.rr-fade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}
</style>
